// traceCodeModeCells.js — unwrap OpenAI/Codex code-mode cells so the existing
// X-ray tracers can read them.
//
// A code-mode agent does not call file/web/query tools at the wire boundary.
// It emits one `exec` cell whose input is a JS program, and the program calls
// `await tools.exec_command({cmd, workdir})`, `tools.apply_patch(patch)`,
// `tools.mcp__…__analytics_query({...})` and so on. The wire shows only the
// cell (name "exec", input.input = the JS source verbatim) plus `wait(cell_id)`
// polls, so every tool-name-keyed extractor reads the whole trace as
// unclassified residual — the work is captured, just wrapped.
//
// unwrapCodeModeTurns() runs BEFORE buildSemanticFeed on the same turns array
// the feed components hold. For each cell it statically extracts the inner
// calls and inserts VIRTUAL blocks the tracers already understand:
//
//   tools.exec_command / shell_command / local_shell
//       → { name: "shell", input: { command } }      (every shell grammar keys
//         on the "shell" name and the `command` arg — forge, queries, shell
//         writes, artifacts, runtime lenses)
//   an apply_patch envelope (*** Begin Patch … *** End Patch) in the source
//       → one { name: "apply_patch", input: { file_path, content } } per added
//         file and { file_path, edits: [{old_string,new_string}] } per updated
//         file — the input SHAPES extractFileOps classifies, so the Files tab
//         renders real diffs (apply_patch's argument is a variable in ~98% of
//         cells, so the envelope is read from the source text, not the call)
//   any other tools.X({literal args})
//       → { name: X, input: args } verbatim — MCP names (analytics_query,
//         search_prior_work, forgejo_api…) match the existing extractors as-is
//
// Virtual blocks are honest about their nature: `virtual: true`, an id of
// `<parentId>#cm<i>` / `<parentId>#pf<i>` so the feed's claim door resolves
// them to the parent cell (push() strips at the first "#"), and a `codeMode`
// tag naming the inner tool. Their results are the cell's output with the
// "Script completed / Wall time / Output:" envelope stripped; a cell answered
// only with "Script running with cell ID N" is completed by the later
// wait(cell_id: N) poll that carries the finished envelope.
//
// Extraction is deliberately static — no execution, ever: the cells are
// model-authored, tenant-supplied code, and this module only READS them. It
// does read harder than a per-call literal parse, though: top-level constant
// bindings propagate into `${ident}` splices and identifier arguments, and a
// call inside .map()/for…of over a known array unrolls into one entry per
// element (see the Abstract evaluation section for the exact bounds). What
// that still can't finish — values computed from outputs, reassigned
// bindings, interpolations richer than a bare identifier — emits nothing;
// the cell falls back to a "code" op via extractCodeCellOps. A multi-call
// cell attaches its one output only when exactly one call was extracted,
// because the cell's stdout is not attributable to a specific call otherwise. Known blind spots, accepted: a regex literal in cell source can
// still confuse the call-boundary masker (maskSource does not model regexes,
// so a `)` inside one can clip an argument early — fewer extractions, never a
// hang; the literal parser itself is regex-aware and every parse loop makes
// unconditional progress), template interpolation is never resolved, and
// `*** Delete File` patch sections emit nothing (no shape says "deleted"
// today).

import { extractCellCallsAst } from "./codeModeAst.js";
import { grammarReady, warmGrammar } from "./syntax/parserHost.js";

// Start the load as soon as anything imports this module, so the grammar is
// almost always ready before a reader reaches a trace. The gate below is the
// safety net for the case where it is not, rather than the common path.
warmGrammar("javascript").catch(() => {});

// Cell-shaped tool names: input.input is a JS program.
const CELL_NAMES = new Set(["exec", "js"]);
// Direct (non-cell) shell calls some Codex captures carry at the top level.
const DIRECT_SHELL_NAMES = new Set(["exec_command", "shell_command", "local_shell"]);
// Inner tools whose argument is a shell command.
const INNER_SHELL_TOOLS = new Set(["exec_command", "shell_command", "local_shell"]);

const CELL_CODE_CAP = 2400;
// How many cells one trace has unwrapped for it. A long Codex session is tens
// of cells; a corpus dump is thousands, and this runs on the render path.
export const MAX_CELLS_PER_TRACE = 400;
const CELL_OUTPUT_CAP = 1600;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

function flattenContent(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((b) => {
      if (typeof b === "string") return b;
      if (b && typeof b.text === "string") return b.text;
      return "";
    })
    .filter(Boolean)
    .join("\n");
}

// ── The output envelope ──────────────────────────────────────────────────────
// "Script completed\nWall time 0.5 seconds\nOutput:\n…" — the wrapper the cell
// host puts around stdout. `output` is everything after "Output:"; `text` is
// the same with the host's own "Exit code: N" and "Warning: truncated output"
// lines lifted off, which is what should feed a grammar or a reader.

const ENVELOPE_RE = /^Script (completed|failed|running with cell ID (\w+))(?:[^\n]*)\nWall time ([\d.]+) seconds?\n(?:Output:\n?)?/;

export function parseCellEnvelope(content) {
  const s = flattenContent(content);
  const m = ENVELOPE_RE.exec(s);
  if (!m) return null;
  const status = m[1] === "completed" ? "completed" : m[1] === "failed" ? "failed" : "running";
  const output = s.slice(m[0].length);
  let text = output;
  let exitCode = null;
  let truncated = false;
  const trunc = /^Warning: truncated output[^\n]*\n(?:Total output lines: \d+\n)?\n?/.exec(text);
  if (trunc) {
    truncated = true;
    text = text.slice(trunc[0].length);
  }
  const exit = /^Exit code: (\d+)\n?/.exec(text);
  if (exit) {
    exitCode = parseInt(exit[1], 10);
    text = text.slice(exit[0].length);
  }
  return {
    status,
    cellId: m[2] ? String(m[2]) : null,
    wallTime: parseFloat(m[3]),
    output,
    text,
    exitCode,
    truncated,
  };
}

// A cell that prints the exec call's RESULT rather than its output -
// `text(JSON.stringify(r))`, or `text(r)`, which the host serializes - hands
// back the whole result object: the command's stdout is its `output` field and
// its status its `exit_code`. Read as stdout, the object put one escaped line
// where a file's lines belong. Only the exec result's own shape qualifies, so a
// command that printed some other JSON with an `output` key is left alone.
// `running` is a result that yielded before the process exited: its output is
// only the start of the stream, with the rest in later stdin polls.
export function parseExecResult(text) {
  const s = String(text || "").trim();
  if (!s.startsWith("{") || !s.endsWith("}")) return null;
  let o;
  try {
    o = JSON.parse(s);
  } catch {
    return null;
  }
  if (!o || typeof o !== "object" || Array.isArray(o) || typeof o.output !== "string") return null;
  // Every exec result observed carries both: its output chunk and its clock.
  if (!("chunk_id" in o) || !("wall_time_seconds" in o)) return null;
  const exitCode = typeof o.exit_code === "number" ? o.exit_code : null;
  return { output: o.output, exitCode, running: exitCode == null && o.session_id != null };
}

// ── JS source scanning ───────────────────────────────────────────────────────
// mask(): the cell source with string and comment interiors blanked to spaces
// (same length), so paren matching and call detection can't be fooled by
// quoted text. Template interpolations are blanked with their template — a
// tools call inside `${…}` is out of reach, by design.

function maskSource(src) {
  const out = src.split("");
  let mode = "code";
  let tplDepth = 0;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    const next = src[i + 1];
    if (mode === "code") {
      if (c === "'") mode = "sq";
      else if (c === '"') mode = "dq";
      else if (c === "`") { mode = "tpl"; tplDepth = 0; }
      else if (c === "/" && next === "/") mode = "lc";
      else if (c === "/" && next === "*") mode = "bc";
      continue;
    }
    if (mode === "lc") {
      if (c === "\n") { mode = "code"; continue; }
      out[i] = " ";
      continue;
    }
    if (mode === "bc") {
      if (c === "*" && next === "/") { out[i] = " "; out[i + 1] = " "; i += 1; mode = "code"; continue; }
      out[i] = c === "\n" ? "\n" : " ";
      continue;
    }
    // Inside a string. Escapes consume the next char; newlines stay so line
    // math survives masking.
    if (c === "\\") {
      out[i] = " ";
      if (i + 1 < src.length) { out[i + 1] = " "; i += 1; }
      continue;
    }
    if (mode === "sq" && c === "'") { mode = "code"; continue; }
    if (mode === "dq" && c === '"') { mode = "code"; continue; }
    if (mode === "tpl") {
      if (c === "`" && tplDepth === 0) { mode = "code"; continue; }
      if (c === "$" && next === "{") tplDepth += 1;
      else if (c === "}" && tplDepth > 0) tplDepth -= 1;
    }
    out[i] = c === "\n" ? "\n" : " ";
  }
  return out.join("");
}

// ── Tolerant literal parsing ─────────────────────────────────────────────────
// Just enough of a JS expression parser to read the object literals code-mode
// cells pass: identifier or string keys, string/template/number/boolean/null
// values, nested objects and arrays. Anything else (a variable, a call, an
// interpolated template) parses as UNRESOLVED — the entry is dropped and the
// object is marked partial, never guessed at.

function skipWs(s, i) {
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i += 1; continue; }
    if (c === "/" && s[i + 1] === "/") { while (i < s.length && s[i] !== "\n") i += 1; continue; }
    if (c === "/" && s[i + 1] === "*") {
      i += 2;
      while (i < s.length && !(s[i] === "*" && s[i + 1] === "/")) i += 1;
      i += 2;
      continue;
    }
    break;
  }
  return i;
}

const ESCAPES = { n: "\n", t: "\t", r: "\r", b: "\b", f: "\f", v: "\v", "0": "\0" };

function parseStringAt(s, i, env = null) {
  const quote = s[i];
  let out = "";
  i += 1;
  while (i < s.length) {
    const c = s[i];
    if (c === "\\") {
      const e = s[i + 1];
      if (e === "u") {
        if (s[i + 2] === "{") {
          const end = s.indexOf("}", i + 3);
          if (end < 0) return null;
          out += String.fromCodePoint(parseInt(s.slice(i + 3, end), 16));
          i = end + 1;
          continue;
        }
        out += String.fromCharCode(parseInt(s.slice(i + 2, i + 6), 16));
        i += 6;
        continue;
      }
      if (e === "x") {
        out += String.fromCharCode(parseInt(s.slice(i + 2, i + 4), 16));
        i += 4;
        continue;
      }
      out += Object.hasOwn(ESCAPES, e) ? ESCAPES[e] : e;
      i += 2;
      continue;
    }
    if (quote === "`" && c === "$" && s[i + 1] === "{") {
      // A `${ident}` splice resolves against the cell's own constant bindings
      // — propagation of a literal the source already states, not evaluation.
      // Anything richer than a bare identifier stays unresolved.
      const m = /^\$\{\s*([A-Za-z_$][\w$]*)\s*\}/.exec(s.slice(i));
      const v = m && env?.has(m[1]) ? env.get(m[1]) : undefined;
      if (typeof v === "string" || typeof v === "number") {
        out += String(v);
        i += m[0].length;
        continue;
      }
      return { interpolated: true };
    }
    if (c === quote) return { value: out, end: i + 1 };
    if (quote !== "`" && c === "\n") return null; // unterminated
    out += c;
    i += 1;
  }
  return null;
}

// `/` only starts a regex literal where an EXPRESSION can start — after an
// operator, an opener, a separator, or at the skipped value's own start; after
// a name, a number, a closer, or a string it is division. The standard
// prior-token heuristic, on single characters because skipValue never builds
// tokens.
const REGEX_BEFORE = new Set("=,([{:;!&|?+-*%<>~^".split(""));

// s[i] is an opening "/". Returns the index past the whole literal (flags
// included), honoring escapes and character classes — `/[,}]/` must not end a
// skip at its comma or strand the scanner on its brace. No closing "/" on the
// same line means it was not a regex after all: consume just the slash.
function skipRegex(s, i) {
  let inClass = false;
  for (let j = i + 1; j < s.length; j++) {
    const c = s[j];
    if (c === "\\") { j += 1; continue; }
    if (c === "\n") break;
    if (inClass) {
      if (c === "]") inClass = false;
      continue;
    }
    if (c === "[") { inClass = true; continue; }
    if (c === "/") {
      let k = j + 1;
      while (k < s.length && /[a-z]/i.test(s[k])) k += 1;
      return k;
    }
  }
  return i + 1;
}

// Skip one value of any shape (string-, regex-, and bracket-aware) so an
// unresolved entry doesn't derail the entries after it. Returns the index just
// past it — which is the SAME index when s[i] is already a delimiter the
// caller owns (a comma, or a closer this value never opened); every caller
// must make progress on its own when that happens.
function skipValue(s, i) {
  let depth = 0;
  let prev = null;
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i += 1; continue; }
    if (c === "'" || c === '"' || c === "`") {
      const q = c;
      i += 1;
      let tpl = 0;
      while (i < s.length) {
        if (s[i] === "\\") { i += 2; continue; }
        if (q === "`" && s[i] === "$" && s[i + 1] === "{") tpl += 1;
        else if (q === "`" && s[i] === "}" && tpl > 0) tpl -= 1;
        else if (s[i] === q && tpl === 0) break;
        i += 1;
      }
      i += 1;
      prev = q;
      continue;
    }
    if (c === "/" && (prev === null || REGEX_BEFORE.has(prev))) {
      i = skipRegex(s, i);
      prev = "/";
      continue;
    }
    if (c === "{" || c === "[" || c === "(") depth += 1;
    else if (c === "}" || c === "]" || c === ")") {
      if (depth === 0) return i;
      depth -= 1;
    } else if (c === "," && depth === 0) return i;
    prev = c;
    i += 1;
  }
  return i;
}

function parseValueAt(s, i, env = null) {
  i = skipWs(s, i);
  const c = s[i];
  if (c === "'" || c === '"' || c === "`") {
    const r = parseStringAt(s, i, env);
    if (!r || r.interpolated) return { resolved: false, end: skipValue(s, i) };
    return { resolved: true, value: r.value, end: r.end };
  }
  if (c === "{") return parseObjectAt(s, i, env);
  if (c === "[") return parseArrayAt(s, i, env);
  const word = /^(true|false|null|undefined)\b/.exec(s.slice(i, i + 10));
  if (word) {
    const v = word[1] === "true" ? true : word[1] === "false" ? false : null;
    return { resolved: true, value: v, end: i + word[1].length };
  }
  const num = /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/.exec(s.slice(i, i + 32));
  if (num) {
    const end = i + num[0].length;
    // `5 * 60` is an expression, not the number 5 — only a number followed by
    // a delimiter is a literal.
    const after = skipWs(s, end);
    if (after >= s.length || ",}]".includes(s[after])) {
      return { resolved: true, value: parseFloat(num[0]), end };
    }
  }
  // A bare identifier bound to a constant reads as that constant — same
  // delimiter rule as numbers, so `total + 1` stays an expression.
  const ident = /^[A-Za-z_$][\w$]*/.exec(s.slice(i, i + 64));
  if (ident && env?.has(ident[0])) {
    const end = i + ident[0].length;
    const after = skipWs(s, end);
    if (after >= s.length || ",}]".includes(s[after])) {
      return { resolved: true, value: env.get(ident[0]), end };
    }
  }
  return { resolved: false, end: skipValue(s, i) };
}

function parseArrayAt(s, i, env = null) {
  const value = [];
  let resolved = true;
  i += 1;
  for (;;) {
    i = skipWs(s, i);
    if (i >= s.length) return { resolved: false, value, end: i };
    if (s[i] === "]") return { resolved, value, end: i + 1 };
    if (s[i] === ",") { i += 1; continue; }
    const v = parseValueAt(s, i, env);
    if (v.resolved) value.push(v.value);
    else resolved = false;
    // The tolerant fallback refuses to consume a delimiter it doesn't own — a
    // stray `}` this array never opened comes back with zero advance, and
    // re-reading it forever would hang the render path. Progress is
    // unconditional.
    i = v.end > i ? v.end : i + 1;
  }
}

function parseObjectAt(s, i, env = null) {
  const value = {};
  let resolved = true;
  i += 1;
  for (;;) {
    i = skipWs(s, i);
    if (i >= s.length) return { resolved: false, value, end: i };
    if (s[i] === "}") return { resolved, value, end: i + 1 };
    if (s[i] === ",") { i += 1; continue; }
    let key = null;
    if (s[i] === "'" || s[i] === '"') {
      const k = parseStringAt(s, i);
      if (!k || k.interpolated) return { resolved: false, value, end: skipValue(s, i) };
      key = k.value;
      i = k.end;
    } else {
      const m = /^[A-Za-z_$][\w$]*/.exec(s.slice(i));
      if (!m) return { resolved: false, value, end: skipValue(s, i) };
      key = m[0];
      i += m[0].length;
    }
    i = skipWs(s, i);
    if (s[i] !== ":") {
      // Shorthand or spread — an entry we can't read.
      resolved = false;
      const j = skipValue(s, i);
      i = j > i ? j : i + 1;
      continue;
    }
    const v = parseValueAt(s, i + 1, env);
    if (v.resolved) value[key] = v.value;
    else resolved = false;
    // Same unconditional-progress rule as parseArrayAt: a zero-advance
    // fallback must not re-read the same character forever.
    i = v.end > i ? v.end : i + 1;
  }
}

// ── Abstract evaluation ──────────────────────────────────────────────────────
// Constant propagation and bounded loop unrolling over the cell's own source —
// still literals-only, still no execution. A cell like
//   const batches = ["591 629", "868 1083"];
//   await Promise.all(batches.map(prs => tools.exec_command({cmd: `… ${prs} …`})))
// states every command it will run; the bindings and the loop shape just have
// to be READ. Bounds keep the reading cheap and honest: only top-level
// const/let/var bindings whose right side is a literal, only bindings never
// reassigned or mutated afterward, only `${bareIdent}` splices, and only
// .map()/for…of over an array binding, capped per loop and per cell.

const ENV_CAP = 64;
const UNROLL_CAP = 16;
const CALLS_PER_CELL_CAP = 48;
// Each recognized loop costs one bodyEnd scan (worst case: to the end of the
// source, when the loop never closes). Unbounded, a crafted cell full of
// `arr.map(x=>arr.map(x=>…` openers would make this pass quadratic on
// tenant-supplied code in the render path; capped, it is at most CAP linear
// scans.
const LOOP_SPANS_CAP = 16;

const escapeIdent = (name) => name.replace(/\$/g, "\\$");

function collectEnv(src, masked) {
  const env = new Map();
  const tainted = new Set();
  const decl = /(?:^|[;{}()\n])\s*(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s*=[^=]/g;
  let m;
  while ((m = decl.exec(masked)) && env.size < ENV_CAP) {
    const name = m[1];
    if (env.has(name) || tainted.has(name)) {
      // Redeclared (a block-scoped shadow this reader doesn't model) — the
      // binding has no single value, so it has none at all, permanently: a
      // third declaration must not resurrect it.
      env.delete(name);
      tainted.add(name);
      continue;
    }
    const eq = masked.indexOf("=", m.index + m[0].indexOf(name) + name.length);
    const v = parseValueAt(src, eq + 1, env);
    if (v.resolved) env.set(name, v.value);
  }
  // A binding assigned or mutated anywhere after its declaration is not a
  // constant; propagating its initial value would state something the cell
  // never ran. One plain assignment is the declaration itself.
  for (const name of [...env.keys()]) {
    const id = escapeIdent(name);
    const assigns = masked.match(new RegExp(`\\b${id}\\s*=[^=]`, "g")) || [];
    const compound = new RegExp(`\\b${id}\\s*[+\\-*/%]=|\\+\\+\\s*${id}\\b|\\b${id}\\s*\\+\\+|--\\s*${id}\\b|\\b${id}\\s*--`);
    const mutation = new RegExp(`\\b${id}\\s*\\.\\s*(?:push|pop|shift|unshift|splice|sort|reverse|fill|copyWithin)\\b`);
    if (assigns.length > 1 || compound.test(masked) || mutation.test(masked)) env.delete(name);
  }
  return env;
}

// The body span of a loop whose elements are known: from `start`, everything
// until the first closer the span itself never opened (the .map's own paren,
// or the for-block's own brace).
function bodyEnd(masked, start) {
  let depth = 0;
  for (let i = start; i < masked.length; i++) {
    const c = masked[i];
    if (c === "(" || c === "{" || c === "[") depth += 1;
    else if (c === ")" || c === "}" || c === "]") {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return masked.length;
}

function loopSpans(masked, env) {
  const spans = [];
  const mapRe = /\b([A-Za-z_$][\w$]*)\s*\.\s*map\s*\(\s*(?:\(\s*([A-Za-z_$][\w$]*)\s*\)|([A-Za-z_$][\w$]*))\s*=>\s*/g;
  let m;
  while (spans.length < LOOP_SPANS_CAP && (m = mapRe.exec(masked))) {
    const elements = env.get(m[1]);
    if (!Array.isArray(elements) || !elements.length) continue;
    const start = m.index + m[0].length;
    spans.push({ param: m[2] || m[3], elements: elements.slice(0, UNROLL_CAP), start, end: bodyEnd(masked, start) });
  }
  const forRe = /\bfor\s*(?:await\s+)?\(\s*(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s+of\s+([A-Za-z_$][\w$]*)\s*\)\s*\{/g;
  while (spans.length < LOOP_SPANS_CAP && (m = forRe.exec(masked))) {
    const elements = env.get(m[2]);
    if (!Array.isArray(elements) || !elements.length) continue;
    const start = m.index + m[0].length;
    spans.push({ param: m[1], elements: elements.slice(0, UNROLL_CAP), start, end: bodyEnd(masked, start) });
  }
  return spans;
}

// One call argument under an environment: an object literal, or a bare
// identifier bound to an object.
function parseArg(argText, env) {
  const at = skipWs(argText, 0);
  if (argText[at] === "{") {
    const p = parseObjectAt(argText, at, env);
    return { resolved: p.resolved, value: p.value };
  }
  const id = /^([A-Za-z_$][\w$]*)\s*$/.exec(argText.slice(at));
  if (id && env?.has(id[1])) {
    const v = env.get(id[1]);
    if (v && typeof v === "object" && !Array.isArray(v)) return { resolved: true, value: v };
  }
  return null;
}

/**
 * Every `tools.X(…)` call in a cell's source, with its argument parsed when it
 * can be READ: a literal, a constant binding, a `${const}` splice — and a call
 * inside .map()/for…of over a known array unrolls into one entry per element,
 * each with the loop variable bound. Returns [{ tool, args, argsResolved,
 * argText, unrolled? }] — `args` is the readable entries (possibly partial),
 * null when the argument was not readable at all.
 */
/**
 * The reader that ships. ONE pipeline: the grammar, or nothing yet.
 *
 * Returns null - not a worse reading - when the JavaScript grammar is not
 * loaded. Two readers running in production means two behaviours to reason
 * about, two sets of bugs, and a trace whose content depends on how recently
 * the tab was opened. The load is 128KB gzipped and about 58ms of init and
 * compile, once per session and zero on every call after, so the honest
 * exchange is a brief wait rather than a permanent second implementation.
 *
 * Callers run inside a synchronous memo and cannot await, so they gate on
 * `codeModeReadable` and show a pending state until it is true.
 */
export function extractCellCalls(source) {
  // EMPTY, never null: a dozen call sites iterate this result, and changing it
  // to a sometimes-null contract turned "the grammar is still loading" into a
  // TypeError in every one of them. Callers that need to tell "no calls here"
  // apart from "not read yet" ask `codeModeReadable`, which is what the UI
  // gates its pending region on.
  return extractCellCallsAst(source) ?? [];
}

/**
 * The original text reader, kept OUT of the shipping path and exported for the
 * differential: a rewrite that cannot be compared against what it replaced is
 * a rewrite nobody can check. Its 40-test contract now runs against the
 * grammar-backed reader, which is what makes it a baseline rather than a
 * second implementation.
 */
export function extractCellCallsText(source) {
  const src = String(source || "");
  if (!src.includes("tools.")) return [];
  const masked = maskSource(src);
  const env = collectEnv(src, masked);
  const spans = env.size ? loopSpans(masked, env) : [];
  const calls = [];
  const head = /\btools\s*\.\s*([A-Za-z_$][\w$]*)\s*\(/g;
  let m;
  while ((m = head.exec(masked)) && calls.length < CALLS_PER_CELL_CAP) {
    const open = m.index + m[0].length - 1;
    let depth = 1;
    let close = -1;
    for (let i = open + 1; i < masked.length; i++) {
      const c = masked[i];
      if (c === "(") depth += 1;
      else if (c === ")") {
        depth -= 1;
        if (depth === 0) { close = i; break; }
      }
    }
    if (close < 0) break;
    const argText = src.slice(open + 1, close);
    const plain = parseArg(argText, env);
    if (!plain?.resolved) {
      // A call a constant can't finish may still be a loop body: re-read it
      // once per element with the loop variable bound. ONE containing loop
      // only — a call under nested known loops runs elements-of-outer ×
      // elements-of-inner times, and unrolling along either single axis would
      // state fewer calls than ran. Nested shapes stay unresolved instead.
      const containing = spans.filter((sp) => m.index >= sp.start && m.index < sp.end);
      const span = containing.length === 1 ? containing[0] : null;
      if (span) {
        const instances = [];
        for (const el of span.elements) {
          const bound = new Map(env);
          bound.set(span.param, el);
          const p = parseArg(argText, bound);
          if (p?.resolved) instances.push({ tool: m[1], args: p.value, argsResolved: true, argText, unrolled: instances.length });
        }
        if (instances.length) {
          calls.push(...instances.slice(0, CALLS_PER_CELL_CAP - calls.length));
          head.lastIndex = close + 1;
          continue;
        }
      }
    }
    calls.push({ tool: m[1], args: plain ? plain.value : null, argsResolved: Boolean(plain?.resolved), argText });
    head.lastIndex = close + 1;
  }
  return calls;
}

// ── apply_patch envelopes ────────────────────────────────────────────────────
// The V4A patch text ("*** Begin Patch … *** End Patch") is present verbatim
// in ~99.8% of apply_patch cells even though the call's argument is a
// variable, so the envelope is read straight from the source. A span shipped
// inside a single-quoted string carries \n escapes; detect and unescape.

function unescapeIfEscaped(span) {
  const real = (span.match(/\n/g) || []).length;
  const escaped = (span.match(/\\n/g) || []).length;
  if (escaped <= real) return span;
  return span
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\`/g, "`")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\");
}

/**
 * Parse every patch envelope in a cell source into per-file operations:
 *   { path, kind: "write", content }                    — *** Add File
 *   { path, kind: "edit", edits: [{old_string,new_string}] } — *** Update File
 * Deleted files and unparseable sections are skipped. `approx` marks a span
 * that contained `${…}` interpolation — the line math is the placeholder's,
 * not the final text's.
 */
export function parsePatchEnvelopes(source) {
  const src = String(source || "");
  const files = [];
  const spanRe = /\*\*\* Begin Patch\n?([\s\S]*?)\*\*\* End Patch/g;
  let m;
  while ((m = spanRe.exec(src))) {
    const span = unescapeIfEscaped(m[1]);
    const approx = span.includes("${");
    const lines = span.split("\n");
    let file = null;
    let hunk = null;
    const closeHunk = () => {
      if (file && hunk && (hunk.old.length || hunk.add.length)) {
        file.edits.push({ old_string: hunk.old.join("\n"), new_string: hunk.add.join("\n") });
      }
      hunk = null;
    };
    const closeFile = () => {
      closeHunk();
      if (!file) return;
      if (file.kind === "write") files.push({ path: file.path, kind: "write", content: file.content.join("\n"), approx });
      else if (file.kind === "edit" && file.edits.length) files.push({ path: file.path, kind: "edit", edits: file.edits, approx });
      file = null;
    };
    for (const line of lines) {
      const add = /^\*\*\* Add File: (.+)$/.exec(line);
      const upd = /^\*\*\* Update File: (.+)$/.exec(line);
      const del = /^\*\*\* Delete File: /.test(line);
      if (add || upd || del) {
        closeFile();
        if (add) file = { kind: "write", path: add[1].trim(), content: [] };
        else if (upd) file = { kind: "edit", path: upd[1].trim(), edits: [] };
        continue;
      }
      if (!file) continue;
      if (/^\*\*\* Move to: /.test(line)) continue;
      if (file.kind === "write") {
        if (line.startsWith("+")) file.content.push(line.slice(1));
        continue;
      }
      if (line.startsWith("@@")) {
        closeHunk();
        hunk = { old: [], add: [] };
        continue;
      }
      if (!hunk) hunk = { old: [], add: [] };
      if (line.startsWith("+")) hunk.add.push(line.slice(1));
      else if (line.startsWith("-")) hunk.old.push(line.slice(1));
      else if (line.startsWith(" ")) {
        hunk.old.push(line.slice(1));
        hunk.add.push(line.slice(1));
      }
    }
    closeFile();
  }
  return files;
}

// ── Shell command normalization ──────────────────────────────────────────────
// exec_command ships `cmd` as a string or as an argv list — usually
// ["bash", "-lc", script]; the script element is the command.

const SHELL_ARGV0_RE = /(?:^|[\\/])(?:ba|z|da)?sh(?:\.exe)?$/i;
const PWSH_ARGV0_RE = /(?:^|[\\/])(?:pwsh|powershell)(?:\.exe)?$/i;

export function normalizeShellCommand(v) {
  if (typeof v === "string") return v || null;
  if (!Array.isArray(v) || !v.length || !v.every((x) => typeof x === "string")) return null;
  if (v.length >= 3 && SHELL_ARGV0_RE.test(v[0]) && /^-l?c$/i.test(v[1])) return v[2];
  if (v.length >= 3 && PWSH_ARGV0_RE.test(v[0]) && /^-c(ommand)?$/i.test(v[1])) return v.slice(2).join(" ");
  return v.join(" ");
}

// ── The unwrap pass ──────────────────────────────────────────────────────────

function isCellBlock(b) {
  return b?.type === "tool_use" && CELL_NAMES.has(String(b.name || "")) && typeof b.input?.input === "string";
}

/** Is this tool_use a code-mode cell (a JS program under an `input` key)? */
export function isCodeModeCell(block) {
  return isCellBlock(block);
}

// One line of envelope facts for an eyebrow or a result label:
// "completed · exit 0 · 11.2s · output truncated by host".
export function cellEnvelopeNote(env) {
  if (!env) return null;
  return [
    env.status,
    env.exitCode != null ? `exit ${env.exitCode}` : null,
    env.wallTime != null ? `${env.wallTime}s` : null,
    env.truncated ? "output truncated by host" : null,
  ].filter(Boolean).join(" · ");
}

/**
 * The cell as the reader should first meet it: the inner command(s), not the
 * JS that issued them. Recovered from the virtual siblings the unwrap spliced
 * in (`siblingBlocks` is the cell's own turn's block list); falls back to
 * static extraction for turns that were never unwrapped. Null when nothing
 * inner is readable — the JS source is then genuinely the best reading.
 */
export function cellInnerReading(block, siblingBlocks) {
  if (!isCellBlock(block) || !block.id) return null;
  const parts = [];
  const tools = [];
  for (const v of siblingBlocks || []) {
    if (!v?.virtual || v.type !== "tool_use" || !String(v.id || "").startsWith(`${block.id}#`)) continue;
    tools.push(v.codeMode?.tool || v.name);
    if (v.name === "shell") parts.push(String(v.input?.command || ""));
    else if (v.name === "apply_patch") parts.push(`apply_patch · ${v.input?.file_path || ""}`);
    else parts.push(`${v.name} ${JSON.stringify(v.input)}`);
  }
  if (!parts.length) {
    for (const call of extractCellCalls(block.input.input)) {
      if (INNER_SHELL_TOOLS.has(call.tool) && call.args) {
        const cmd = normalizeShellCommand(call.args.cmd ?? call.args.command);
        if (cmd) {
          parts.push(cmd);
          tools.push(call.tool);
          continue;
        }
      }
      tools.push(call.tool);
    }
  }
  if (!parts.length) return null;
  const read = parts.filter(Boolean);
  return { text: read.join("\n"), count: read.length, tools: [...new Set(tools)] };
}

function isDirectShellBlock(b) {
  if (b?.type !== "tool_use" || !DIRECT_SHELL_NAMES.has(String(b.name || ""))) return false;
  const v = b.input?.cmd ?? b.input?.command;
  return typeof v === "string" || Array.isArray(v);
}

export function isWaitBlock(b) {
  return b?.type === "tool_use" && String(b.name || "") === "wait" && b.input?.cell_id != null;
}

function shellVirtualInput(args) {
  const command = normalizeShellCommand(args?.cmd ?? args?.command);
  if (!command) return null;
  const input = { command };
  if (str(args?.workdir)) input.workdir = args.workdir;
  return input;
}

/**
 * Does this trace hold any code-mode cells? Cheap gate so non-Codex traces pay
 * one scan and nothing else.
 */
export function hasCodeModeCells(turns) {
  for (const turn of Array.isArray(turns) ? turns : []) {
    for (const b of turn?.blocks || []) {
      if (isCellBlock(b) || isDirectShellBlock(b)) return true;
    }
  }
  return false;
}

// The readiness gate's narrower question: only genuine cells carry JS the
// grammar has to read. Direct shell blocks unwrap without the parser, so they
// must not hold a trace pending on a load they never use.
function hasGrammarCells(turns) {
  for (const turn of Array.isArray(turns) ? turns : []) {
    for (const b of turn?.blocks || []) {
      if (isCellBlock(b)) return true;
    }
  }
  return false;
}

/**
 * Rewrite turns with virtual inner blocks after each code-mode cell (and each
 * cell's result). Identity when the trace has none. The returned array must be
 * the ONE array both buildSemanticFeed and the feed components hold — the
 * inserted blocks shift block indices within a turn, so mixing unwrapped and
 * raw turns would break (turn, block) coordinates.
 */
/**
 * Whether this trace's cells can be read RIGHT NOW.
 *
 * True when there is nothing the grammar has to read - a conversation with no
 * code-mode cells never waits for a grammar it does not need, and a trace
 * carrying only direct shell blocks unwraps without the parser - or when the
 * grammar is loaded. Callers render a pending region while this is false
 * rather than showing a reading assembled by a second, weaker parser.
 */
export function codeModeReadable(turns) {
  if (!hasGrammarCells(turns)) return true;
  return grammarReady("javascript");
}

/**
 * wait(cell_id) polls finish cells whose own result only said "running".
 * Keeps the finished envelope per cell id, else the latest poll's.
 */
export function waitEnvelopesByCell(turns, resultById) {
  const out = new Map();
  for (const turn of Array.isArray(turns) ? turns : []) {
    for (const b of turn?.blocks || []) {
      if (!isWaitBlock(b) || !b.id) continue;
      const env = parseCellEnvelope(resultById.get(b.id)?.content);
      if (!env) continue;
      const cellId = String(b.input.cell_id);
      const prev = out.get(cellId);
      if (!prev || prev.status === "running" || env.status !== "running") out.set(cellId, env);
    }
  }
  return out;
}

/**
 * A cell's result with the host's envelope read: its stdout and whether it
 * failed. `singleShell` says the cell made exactly one shell call, which makes
 * a printed exec result object that call's own result.
 */
export function cellOutcome(resultBlock, waitEnvByCell, singleShell) {
  const env = parseCellEnvelope(resultBlock.content);
  const finalEnv = env?.status === "running" && env.cellId && waitEnvByCell.has(env.cellId)
    ? waitEnvByCell.get(env.cellId)
    : env;
  let text = finalEnv ? finalEnv.text : flattenContent(resultBlock.content);
  let isError = finalEnv?.exitCode != null ? finalEnv.exitCode !== 0 : Boolean(resultBlock.isError);
  const printed = singleShell ? parseExecResult(text) : null;
  if (printed) {
    // A partial stream is withheld rather than attached: a reader would
    // take its last, cut-off line for a line of the file.
    text = printed.running ? "" : printed.output;
    if (printed.exitCode != null) isError = printed.exitCode !== 0;
  }
  return { text, isError };
}

export function unwrapCodeModeTurns(turns) {
  if (!Array.isArray(turns) || !hasCodeModeCells(turns)) return turns;

  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id && !resultById.has(b.tool_use_id)) {
        resultById.set(b.tool_use_id, b);
      }
    }
  }

  const waitEnvByCell = waitEnvelopesByCell(turns, resultById);

  // Plan virtual blocks per parent cell, then splice in one rebuild pass.
  const vbsByParent = new Map(); // parentId → [tool_use blocks]
  const vresByParent = new Map(); // parentId → [tool_result blocks]
  // Cells parsed so far. This walk is synchronous and runs inside a render-path
  // memo, so a conversation carrying hundreds of cells would hold the thread
  // for as long as parsing them takes. The shell door bounds itself the same
  // way; this had no bound at all. Past the cap a cell keeps its own block and
  // simply gains no virtual children, which is the state every cell was in
  // before this reading existed.
  let parsed = 0;

  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      const P = b?.id;
      if (!P) continue;

      let vbs = null;
      if (isCellBlock(b) && parsed < MAX_CELLS_PER_TRACE) {
        parsed += 1;
        const source = b.input.input;
        vbs = [];
        extractCellCalls(source).forEach((call, i) => {
          if (call.tool === "apply_patch") return; // envelope pass below
          let input = null;
          if (INNER_SHELL_TOOLS.has(call.tool)) {
            input = call.args ? shellVirtualInput(call.args) : null;
            if (input) vbs.push({ type: "tool_use", id: `${P}#cm${i}`, name: "shell", input, virtual: true, codeMode: { parent: P, tool: call.tool } });
            return;
          }
          if (call.args && call.argsResolved) {
            vbs.push({ type: "tool_use", id: `${P}#cm${i}`, name: call.tool, input: call.args, virtual: true, codeMode: { parent: P, tool: call.tool } });
          }
        });
        parsePatchEnvelopes(source).forEach((f, j) => {
          const input = f.kind === "write"
            ? { file_path: f.path, content: f.content }
            : { file_path: f.path, edits: f.edits };
          vbs.push({ type: "tool_use", id: `${P}#pf${j}`, name: "apply_patch", input, virtual: true, codeMode: { parent: P, tool: "apply_patch", approx: f.approx } });
        });
      } else if (isDirectShellBlock(b)) {
        const input = shellVirtualInput(b.input);
        if (input) vbs = [{ type: "tool_use", id: `${P}#cm0`, name: "shell", input, virtual: true, codeMode: { parent: P, tool: String(b.name) } }];
      }
      if (!vbs || !vbs.length) continue;
      vbsByParent.set(P, vbs);

      const R = resultById.get(P);
      if (!R) continue;
      // One extracted call owns the cell's whole output; several share an
      // unattributable stdout, so they carry only the outcome.
      const callVbs = vbs.filter((v) => v.name !== "apply_patch");
      const { text: outputText, isError } = cellOutcome(R, waitEnvByCell, callVbs.length === 1 && callVbs[0].name === "shell");
      const vres = vbs.map((v) => ({
        type: "tool_result",
        tool_use_id: v.id,
        content: v.name !== "apply_patch" && callVbs.length === 1 ? outputText : "",
        isError,
        linkedToolName: v.name,
        virtual: true,
      }));
      vresByParent.set(P, vres);
    }
  }

  if (!vbsByParent.size) return turns;

  return turns.map((turn) => {
    const blocks = turn?.blocks;
    if (!Array.isArray(blocks)) return turn;
    let touched = false;
    const out = [];
    for (const b of blocks) {
      out.push(b);
      if (b?.type === "tool_use" && b.id && vbsByParent.has(b.id)) {
        out.push(...vbsByParent.get(b.id));
        touched = true;
      } else if (b?.type === "tool_result" && b.tool_use_id && vresByParent.has(b.tool_use_id)) {
        out.push(...vresByParent.get(b.tool_use_id));
        vresByParent.delete(b.tool_use_id); // duplicated results must not duplicate virtuals
        touched = true;
      }
    }
    return touched ? { ...turn, blocks: out } : turn;
  });
}

// ── The "code" feed domain ───────────────────────────────────────────────────
// What remains cell-shaped after the unwrap: cells nothing could be extracted
// from (pure-JS compute, unresolvable arguments), wait polls, and stdin sends.
// Cells that DID emit virtual blocks are represented by those blocks' claims —
// a second op here would double-render 94% of the trace.

function cellOpOf(b, result, command, commandCount = command ? 1 : 0) {
  const source = b.input.input;
  const env = parseCellEnvelope(result?.content);
  const output = env ? env.text : flattenContent(result?.content);
  return {
    key: b.id,
    kind: "cell",
    command: command || null,
    commandCount,
    code: source.length > CELL_CODE_CAP ? source.slice(0, CELL_CODE_CAP) : source,
    codeTruncated: source.length > CELL_CODE_CAP,
    lines: source.split("\n").length,
    tools: [...new Set(extractCellCalls(source).map((c) => c.tool))],
    output: output.length > CELL_OUTPUT_CAP ? output.slice(0, CELL_OUTPUT_CAP) : output || null,
    outputTruncated: output.length > CELL_OUTPUT_CAP,
    wallTime: env?.wallTime ?? null,
    ok: env?.exitCode != null ? env.exitCode === 0 : !result?.isError,
  };
}

export function extractCodeCellOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id && !resultById.has(b.tool_use_id)) {
        resultById.set(b.tool_use_id, b);
      }
    }
  }
  for (const turn of turns) {
    const blocks = turn?.blocks || [];
    for (const b of blocks) {
      if (!b?.id) continue;
      const result = resultById.get(b.id);
      const ok = !result?.isError;
      if (isCellBlock(b)) {
        const hasVirtual = blocks.some((v) => v?.virtual && String(v.id || "").startsWith(`${b.id}#`));
        if (hasVirtual) continue;
        ops.push(cellOpOf(b, result, null));
        continue;
      }
      if (isWaitBlock(b)) {
        const env = parseCellEnvelope(result?.content);
        ops.push({ key: b.id, kind: "poll", cellId: String(b.input.cell_id), status: env?.status ?? null, ok });
        continue;
      }
      if (b.type === "tool_use" && String(b.name || "") === "write_stdin") {
        ops.push({ key: b.id, kind: "stdin", ok });
      }
    }
  }
  return ops;
}

/**
 * The feed's last-resort claim: a cell whose unwrapped work no extractor
 * recognized. The cell is still fully READ — source, inner command, output —
 * so it belongs in the code domain with the command as its face, not in the
 * residual as an opaque `exec` row. Runs after every other extractor;
 * `claimedIds` keeps it off cells someone already interpreted (including the
 * zero-extraction cells extractCodeCellOps claims itself).
 */
export function fallbackCodeCellOps(turns, claimedIds) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id && !resultById.has(b.tool_use_id)) {
        resultById.set(b.tool_use_id, b);
      }
    }
  }
  for (const turn of turns) {
    const blocks = turn?.blocks || [];
    for (const b of blocks) {
      if (!isCodeModeCell(b) || !b.id || claimedIds?.has(b.id)) continue;
      const inner = cellInnerReading(b, blocks);
      ops.push(cellOpOf(b, resultById.get(b.id), inner?.text || null, inner?.count || 0));
    }
  }
  return ops;
}
